import React, { useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Box from "./Box";
import CustomSingleSelect, { CustomSingleSelectModel } from "./CustomSingleSelect";
import { Helpers } from "../helpers/Helpers";
import { storeActivityIndicator, storeFastVehicle } from "../redux/reducers/authSlice";
import { ConstantService } from "../services/ConstantService";
import { VehicleService } from "../services/VehicleService";
import { CompanyService } from "../services/CompanyService";
import { AddVehicleRequest } from "../models/request/vehicle/AddVehicleRequest";
import { AddVehicleResponse, AddVehicleResponseData } from "../models/response/vehicle/AddVehicleResponse";

/** Dorse plakasi girilebilen araç cinsleri (araç tanımlama ekranındaki kuralın aynısı). */
const DORSE_GEREKTIREN_ARAC_CINSLERI = ['2', '4'];

/** Dorse kaydının araç cinsi: Treyler (Dorse). */
const TREYLER_ARAC_CINSI_KODU = '6';

/** Dorse kaydının araç durumu: TEDARİK. */
const TEDARIK_ARAC_DURUMU_KODU = '3';

export interface FastVehicleProps {
    /** Bir önceki adımda tanımlanan firma; araç sahibi olarak kilitli gelir. */
    ownerCompanyId: string
    ownerCompanyName: string
    /** Bir önceki adımda tanımlanan sürücü; kilitli gelir. */
    driverId: string
    driverName: string
    /** "Firma tanımlı" akışında true: Araç Sahibi sürücünün firmasıyla dolu gelir ama değiştirilebilir. */
    ownerEditable?: boolean
    onSubmit: (vehicle: AddVehicleResponseData, trailer: AddVehicleResponseData | undefined) => void
}

/**
 * Hızlı Araç tanımlama paneli. Dorse plakası girildiğinde önce dorse aracı
 * (Treyler cinsi, Tedarik durumu, sahibi aynı firma) kaydedilir, sonra ana araca bağlanır.
 */
const FastVehicle = ({ ownerCompanyId, ownerCompanyName, driverId, driverName, ownerEditable, onSubmit }: FastVehicleProps) => {
    const dispatch = useDispatch();

    const [secilenSahip, setSecilenSahip] = useState<CustomSingleSelectModel | undefined>({ label: ownerCompanyName, value: ownerCompanyId });
    const sahipId = ownerEditable ? secilenSahip?.value : ownerCompanyId;

    const [selectedVehicleStatus, setSelectedVehicleStatus] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [selectedVehicleType, setSelectedVehicleType] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [selectedVehicleType2, setSelectedVehicleType2] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [plate, setPlate] = useState('');
    const [trailerPlate, setTrailerPlate] = useState('');

    /** Dorse plakası yalnız çekici ve kamyon cinslerinde istenir. */
    const dorseVarMi = selectedVehicleType != undefined && DORSE_GEREKTIREN_ARAC_CINSLERI.includes(selectedVehicleType.additionalValue1 + '');

    const kapat = () => dispatch(storeFastVehicle(undefined));

    /** Sabit listesinden koda göre kayıt bulur (dorse aracının cinsi ve durumu için). */
    const sabitBul = async (kategoriKodu: string, kod: string) => {
        var response: any = await ConstantService.getConstantList(kategoriKodu);

        if (!response?.isSuccess)
            throw response?.message ?? (kategoriKodu + ' listesi okunamadı.');

        return (response.data ?? []).find((x: any) => x.additionalValue1 + '' == kod);
    }

    const saveVehicle = async () => {
        try {
            if (!selectedVehicleStatus)
                toast.error('Araç Durumu alanını doldurunuz!')
            else if (!selectedVehicleType)
                toast.error('Araç Cinsi alanını doldurunuz!')
            else if (Helpers.isEmpty(plate))
                toast.error('Araç Plakası alanını doldurunuz!')
            else if (dorseVarMi && Helpers.isEmpty(trailerPlate))
                toast.error('Dorse Plakası alanını doldurunuz!')
            else if (!sahipId)
                toast.error('Araç Sahibi alanını doldurunuz!')
            else {
                dispatch(storeActivityIndicator(true));

                var trailer: AddVehicleResponseData | undefined = undefined;

                // Once dorse araci kaydedilir; ana arac ona baglanacak.
                if (dorseVarMi) {
                    var treylerCinsi = await sabitBul('VEHICLE_TYPE', TREYLER_ARAC_CINSI_KODU);
                    var tedarikDurumu = await sabitBul('VEHICLE_STATUS', TEDARIK_ARAC_DURUMU_KODU);

                    if (!treylerCinsi || !tedarikDurumu) {
                        toast.error('Dorse için Treyler araç cinsi ya da Tedarik araç durumu tanımı bulunamadı.');
                        dispatch(storeActivityIndicator(false));
                        return;
                    }

                    var trailerObj: AddVehicleRequest = {
                        vehicleId: undefined,
                        plate: trailerPlate,
                        vehicleStatusId: tedarikDurumu.constantId,
                        vehicleTypeId: treylerCinsi.constantId,
                        vehicleOwnerId: sahipId,
                        driverId: undefined,
                        isBlocked: 0,
                        isCustomActive: 1,
                        vehiclePassCards: [],
                        periodicalDocuments: [],
                        fileArchives: []
                    };

                    var trailerResponse: AddVehicleResponse = await VehicleService.addVehicle(trailerObj);

                    if (!trailerResponse.isSuccess) {
                        toast.error(trailerResponse.message);
                        dispatch(storeActivityIndicator(false));
                        return;
                    }

                    trailer = trailerResponse.data;
                }

                var obj: AddVehicleRequest = {
                    vehicleId: undefined,
                    plate: plate,
                    vehicleStatusId: selectedVehicleStatus.value,
                    vehicleTypeId: selectedVehicleType.value,
                    vehicleType2Id: selectedVehicleType2?.value,
                    vehicleOwnerId: sahipId,
                    driverId: driverId,
                    trailerId: trailer?.vehicleId,
                    isBlocked: 0,
                    isCustomActive: 1,
                    vehiclePassCards: [],
                    periodicalDocuments: [],
                    fileArchives: []
                };

                var response: AddVehicleResponse = await VehicleService.addVehicle(obj);

                if (!response.isSuccess) {
                    toast.error(response.message);
                    dispatch(storeActivityIndicator(false));
                    return;
                }

                dispatch(storeActivityIndicator(false));
                kapat();
                onSubmit(response.data, trailer);
            }
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, background: 'rgba(0, 0, 0, 0.66)', zIndex: 999, width: '100%', height: '100%' }}>
            <Box width="100%" height="100%" flexDirection="row">
                <Box onClick={kapat} flexGrow={1}>&nbsp;</Box>

                <Box width="400px" height="100%" alignSelf="flex-end" bg='white'>
                    <Box className="mb-10" flexDirection="row" pl="15px" pr="15px" pt="15px">
                        <Box flexGrow={1} justifyContent="center">
                            <h4 className="m-0 text-black">Hızlı Araç İşlemleri</h4>
                        </Box>
                        <Box justifyContent="center">
                            <i onClick={kapat} style={{ fontSize: 18 }} className="actionIcon text-muted fa fa-times" />
                        </Box>
                    </Box>

                    <Box overflowY="auto" pl="15px" pr="15px">
                        <div>
                            <label className="form-label required">Araç Durumu</label>
                            <CustomSingleSelect
                                menuPortal
                                selectedValue={selectedVehicleStatus}
                                onChange={setSelectedVehicleStatus}
                                onMenuOpen={() => ConstantService.getConstantList('VEHICLE_STATUS')}
                                labelName='value'
                                valueName='constantId'
                            />
                        </div>

                        <div className="mt-5">
                            <label className="form-label required">Araç Cinsi</label>
                            <CustomSingleSelect
                                menuPortal
                                selectedValue={selectedVehicleType}
                                onChange={(selectedItem) => {
                                    setSelectedVehicleType(selectedItem);

                                    // Dorse istemeyen bir cinse geçilirse girilen plaka temizlenir.
                                    if (!DORSE_GEREKTIREN_ARAC_CINSLERI.includes(selectedItem?.additionalValue1 + ''))
                                        setTrailerPlate('');
                                }}
                                onMenuOpen={() => ConstantService.getConstantList('VEHICLE_TYPE')}
                                labelName='value'
                                valueName='constantId'
                                additionalValue1Name='additionalValue1'
                            />
                        </div>

                        <div className="mt-5">
                            <label className="form-label required">Araç Plakası</label>
                            <input style={{ border: '1px solid #F1F1F4' }} value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} type="text" className="form-control form-control-solid" />
                        </div>

                        {
                            dorseVarMi && (
                                <div className="mt-5">
                                    <label className="form-label required">Dorse Plakası</label>
                                    <input style={{ border: '1px solid #F1F1F4' }} value={trailerPlate} onChange={(e) => setTrailerPlate(e.target.value.toUpperCase())} type="text" className="form-control form-control-solid" />
                                    <span className="text-muted" style={{ fontSize: 11 }}>Treyler (Dorse) cinsinde, Tedarik durumunda yeni araç olarak kaydedilir.</span>
                                </div>
                            )
                        }

                        {/* Bir onceki adimda tanimlanan surucu; degistirilemez. */}
                        <div className="mt-5">
                            <label className="form-label required">Sürücü</label>
                            <input disabled style={{ border: '1px solid #F1F1F4' }} value={driverName} type="text" className="form-control form-control-solid" />
                        </div>

                        <div className="mt-5">
                            <label className="form-label">Araç Cinsi 2</label>
                            <CustomSingleSelect
                                menuPortal
                                selectedValue={selectedVehicleType2}
                                onChange={setSelectedVehicleType2}
                                onMenuOpen={() => ConstantService.getConstantList('VEHICLE_TYPE_2')}
                                labelName='value'
                                valueName='constantId'
                            />
                            <div className="form-text text-muted">Bazı anlaşmalarda bu alan kullanılmaktadır.</div>
                        </div>

                        {/* Önceki adımın firması; "Firma tanımlı" akışında dolu gelir ve değiştirilebilir. */}
                        <div className="mt-5">
                            {
                                ownerEditable
                                    ? <CustomSingleSelect
                                        labelText="Araç Sahibi"
                                        isLabelRequired
                                        selectedValue={secilenSahip}
                                        onChange={setSecilenSahip}
                                        onMenuOpen={() => CompanyService.getCompanyList({ isSupplier: true })}
                                        labelName='name'
                                        valueName='companyId'
                                    />
                                    : <React.Fragment>
                                        <label className="form-label required">Araç Sahibi</label>
                                        <input disabled style={{ border: '1px solid #F1F1F4' }} value={ownerCompanyName} type="text" className="form-control form-control-solid" />
                                    </React.Fragment>
                            }
                        </div>

                        <div className="mt-5">
                            <span className="text-muted" style={{ fontSize: 11 }}>Araç aktif olarak kaydedilir.</span>
                        </div>
                    </Box>

                    <Box flexDirection="row" justifyContent="flex-end" p="15px">
                        <Box mr="15px">
                            <button className="btn btn-sm btn-light btn-active-light-primary" onClick={kapat}>Geri</button>
                        </Box>
                        <Box>
                            <button className="btn btn-primary btn-sm" onClick={saveVehicle}>Kaydet</button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </div>
    )
}

export default FastVehicle;
